import React from 'react';
import { PromiseState } from 'react-refetch';
import KPIWrapper from '../KPIWrapper/KPIWrapper';
import KPILoader from 'components/utility/ContentLoader/KPILoader/KPILoader';
import delve from 'dlv';

import { ReactComponent as PhysicalIcon } from 'icons/topics/physical.svg';
import KeyValueGroup from 'components/base/KeyValueGroup/KeyValueGroup';

import {
  Container,
  KPIList,
  KPIListItem,
  Header,
  TWTDText,
  DateRange,
} from '../shared';

import mapKPIData from '../mappings/mapKPIData';
import apiConnect from 'helpers/apiConnect';
import formatDate from 'helpers/formatDate';
import useDateRangeKPI from 'hooks/useDateRangeKPI';
import useMedia from 'hooks/useMedia';

import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import getPhysicalKPIURL from 'url-constructors/getPhysicalKPIURL';
import useExportURL from 'hooks/useExportURL';
import {
  TopicFilterNames,
  ITopicFilterState,
} from 'components/TopicFilters/shared';
import { EntityTypes } from 'context/entityContext';
import { IEntity, IIterator } from 'types/shared';
import stringToSlug from 'helpers/stringToSlug';
import { tooltipContent } from 'helpers/getTooltipContent';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import { IToolbarItemType } from 'components/utility/GenericComponent/GenericComponent';

interface IEntityData extends IIterator {
  data: {
    count_last_last_week: number;
    count_last_week: number;
    count_last_week_to_date: number;
    count_rtd: number;
    count_this_week_to_date: number;
    wow_in_progress: number;
    wow_last_completed: number;
  };
  entity: IEntity;
}

interface IPhysicalKPIsProps extends IApiConnectProps {
  allowedFilters: TopicFilterNames[];
  compareMode: string;
  dataFetch: PromiseState<IEntityData[]>;
  error: any;
}

interface IOuterProps {
  compareMode: string;
  countryIds: string[];
  dataColors: string[];
  entityIds: string[];
  customRegionFilter?: string | string[];
}

interface IApiConnectProps extends IOuterProps {
  entityType: EntityTypes;
  filters: ITopicFilterState;
  customRegion?: string | string[];
}

const dataSources = ({
  entityType,
  entityIds,
  compareMode,
  countryIds,
  filters,
  customRegionFilter,
}: IApiConnectProps): any => ({
  dataFetch: getPhysicalKPIURL({
    entityIds,
    entityType,
    countryIds: compareMode === 'country' ? countryIds : filters?.country,
    region: filters?.region,
    compareMode,
    customRegion: customRegionFilter,
  }),
});

const PhysicalKPIs: React.FC<IPhysicalKPIsProps> = (props) => {
  const {
    allowedFilters,
    compareMode,
    countryIds,
    dataColors,
    dataFetch,
    error,
    entityIds,
  } = props;

  const isMobileView = useMedia('(max-width: 40em)');

  const [thisWeekToDate, lastWeek] = useDateRangeKPI('physical');

  const compareArray = compareMode === 'country' ? countryIds : entityIds;

  const physicalData = mapKPIData({
    data: dataFetch.value,
    compareMode,
    compareArray,
  });

  const exportUrl = useExportURL(dataFetch);
  const errorCause = dataFetch?.reason;
  const currentError = error || errorCause?.message;

  return (
    <Container data-pw={stringToSlug('Physical KPIs')}>
      <ErrorBoundary
        error={currentError ? new Error(currentError) : null}
        errorInfo={errorCause?.cause}
      >
        <KPIWrapper
          allowedFilters={allowedFilters}
          glossarySlug="kpis/physical-kpis"
          title="Physical KPIs"
          icon={PhysicalIcon}
          extraToolbarItems={[
            {
              label: 'Export Physical KPI data',
              href: exportUrl,
              type: IToolbarItemType.Export,
              order: 1,
            },
          ]}
          titleAlignLeft="-1.5em"
          alignSVG="2em"
        >
          <KPIList>
            <KPIListItem>
              <Header>
                <TippyToolTip
                  enabled
                  tooltipContent={tooltipContent}
                  offset={[10, 25]}
                  maxWidth="170px"
                >
                  <TWTDText>{`This Week ${
                    !isMobileView ? 'to Date' : ''
                  }`}</TWTDText>
                </TippyToolTip>
                {thisWeekToDate && (
                  <DateRange>
                    {`${formatDate(thisWeekToDate?.startDate, 'day', true)} 
                    - ${formatDate(thisWeekToDate?.endDate, 'day', true)}`}
                  </DateRange>
                )}
              </Header>
              {!physicalData && <KPILoader showPrevious showChange />}
              {physicalData && (
                <KeyValueGroup
                  items={[
                    {
                      label: 'Shipped units',
                      metric: delve(physicalData, 'count_this_week_to_date'),
                    },
                    {
                      label: 'Previous',
                      metric: delve(physicalData, 'count_last_week_to_date'),
                    },
                    {
                      label: 'Change',
                      metric: delve(physicalData, 'wow_in_progress'),
                    },
                  ]}
                  dataColors={dataColors}
                  compareMode={compareMode}
                  isKPIValue
                />
              )}
            </KPIListItem>
            <KPIListItem>
              <Header>
                <TWTDText>
                  {`Last ${!isMobileView ? 'Full ' : ''}Week`}
                </TWTDText>
                {lastWeek && (
                  <DateRange>
                    {`${formatDate(lastWeek?.startDate, 'day', true)} 
                  - ${formatDate(lastWeek?.endDate, 'day', true)}`}
                  </DateRange>
                )}
              </Header>
              {!physicalData && <KPILoader showPrevious showChange />}
              {physicalData && (
                <KeyValueGroup
                  items={[
                    {
                      label: 'Shipped units',
                      metric: delve(physicalData, 'count_last_week'),
                    },
                    {
                      label: 'Previous',
                      metric: delve(physicalData, 'count_last_last_week'),
                    },
                    {
                      label: 'Change',
                      metric: delve(physicalData, 'wow_last_completed'),
                    },
                  ]}
                  dataColors={dataColors}
                  compareMode={compareMode}
                  isKPIValue
                />
              )}
            </KPIListItem>
            <KPIListItem>
              <Header>Release to date</Header>
              {!physicalData && <KPILoader />}
              {physicalData && (
                <KeyValueGroup
                  items={[
                    {
                      label: 'Shipped units',
                      metric: delve(physicalData, 'count_rtd'),
                    },
                  ]}
                  dataColors={dataColors}
                  compareMode={compareMode}
                />
              )}
            </KPIListItem>
          </KPIList>
        </KPIWrapper>
      </ErrorBoundary>
    </Container>
  );
};

export default apiConnect<IApiConnectProps>(dataSources)(
  PhysicalKPIs as any
) as any;
